/**
 * Product Image Slider with Swipe Support
 * Handles touch/mouse swipe gestures for product card image galleries
 */

class ProductImageSlider {
  constructor(sliderElement) {
    this.slider = sliderElement;
    this.slides = sliderElement.querySelectorAll('.product-image-slide');
    
    // Find dots - they should be in the same card__media container
    const mediaContainer = sliderElement.closest('.card__media') || sliderElement.closest('.media');
    this.dotsContainer = mediaContainer ? mediaContainer.querySelector('.product-image-dots') : null;
    this.dots = this.dotsContainer ? this.dotsContainer.querySelectorAll('.product-image-dot') : [];
    
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    
    // Touch/swipe tracking
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.isDragging = false;
    this.startTime = 0;
    
    // Configuration
    this.threshold = 30; // Minimum distance for swipe (px) - reduced for better sensitivity
    this.velocityThreshold = 0.3; // Minimum velocity for swipe
    this.allowedTime = 400; // Maximum time for swipe (ms) - increased for easier swiping
    
    this.init();
  }
  
  init() {
    if (this.totalSlides <= 1) {
      return;
    }
    
    // Add touch event listeners
    this.slider.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.slider.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.slider.addEventListener('touchend', this.handleTouchEnd.bind(this));
    
    // Add mouse event listeners for desktop
    this.slider.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.slider.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.slider.addEventListener('mouseup', this.handleMouseEnd.bind(this));
    this.slider.addEventListener('mouseleave', this.handleMouseEnd.bind(this));
    
    // Prevent default drag behavior
    this.slider.addEventListener('dragstart', (e) => e.preventDefault());
    
    // Prevent drag on images
    this.slides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img) {
        img.setAttribute('draggable', 'false');
        img.style.pointerEvents = 'none';
      }
    });
    
    // Add click listeners to dots
    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToSlide(index);
        });
      });
    }
    
    // Set initial state
    this.updateSlider();
  }
  
  // Touch Events
  handleTouchStart(e) {
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.startTime = Date.now();
    this.isDragging = true;
  }
  
  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    this.currentX = e.touches[0].clientX;
    this.currentY = e.touches[0].clientY;
    
    // Calculate distances
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    
    // Prevent vertical scrolling if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
    }
  }
  
  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    const deltaTime = Date.now() - this.startTime;
    const velocity = Math.abs(deltaX) / deltaTime;
    
    // Check if it's a valid swipe
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isQuickSwipe = deltaTime < this.allowedTime;
    const isFastEnough = velocity > this.velocityThreshold;
    const isLongEnough = Math.abs(deltaX) > this.threshold;
    
    if (isHorizontalSwipe && (isQuickSwipe && isFastEnough || isLongEnough)) {
      if (deltaX > 0) {
        // Swipe right - go to previous
        this.previousSlide();
      } else {
        // Swipe left - go to next
        this.nextSlide();
      }
    }
    
    this.isDragging = false;
  }
  
  // Mouse Events
  handleMouseDown(e) {
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startTime = Date.now();
    this.isDragging = true;
    this.slider.style.cursor = 'grabbing';
  }
  
  handleMouseMove(e) {
    if (!this.isDragging) return;
    
    this.currentX = e.clientX;
    this.currentY = e.clientY;
  }
  
  handleMouseEnd(e) {
    if (!this.isDragging) return;
    
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    const deltaTime = Date.now() - this.startTime;
    const velocity = Math.abs(deltaX) / deltaTime;
    
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isQuickSwipe = deltaTime < this.allowedTime;
    const isFastEnough = velocity > this.velocityThreshold;
    const isLongEnough = Math.abs(deltaX) > this.threshold;
    
    if (isHorizontalSwipe && (isQuickSwipe && isFastEnough || isLongEnough)) {
      if (deltaX > 0) {
        this.previousSlide();
      } else {
        this.nextSlide();
      }
    }
    
    this.isDragging = false;
    this.slider.style.cursor = 'grab';
  }
  
  // Navigation methods
  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
    this.updateSlider();
  }
  
  previousSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.updateSlider();
  }
  
  goToSlide(index) {
    this.currentSlide = index;
    this.updateSlider();
  }
  
  updateSlider() {
    // Update slider transform
    const translateX = -this.currentSlide * 100;
    this.slider.style.transform = `translateX(${translateX}%)`;
    
    // Update dots if they exist
    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        if (index === this.currentSlide) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
  }
}

// Initialize all sliders when DOM is ready
function initProductSliders() {
  const sliders = document.querySelectorAll('.product-image-slider');
  
  sliders.forEach((slider, index) => {
    try {
      // Check if already initialized
      if (slider.hasAttribute('data-slider-initialized')) {
        return;
      }
      
      // Mark as initialized
      slider.setAttribute('data-slider-initialized', 'true');
      
      // Initialize the slider
      new ProductImageSlider(slider);
    } catch (error) {
      throw new Error(error)
    }
  });
}

// Run on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductSliders);
} else {
  // DOM already loaded
  initProductSliders();
}

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initProductSliders();
  });
}

// Export for manual initialization if needed
if (typeof window !== 'undefined') {
  window.ProductImageSlider = ProductImageSlider;
  window.initProductSliders = initProductSliders;
}